import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import clsx from 'clsx';

import LookerReport from '@/src/components/project/Dashboard/components/LookerReport/LookerReport';
import NotAvailable from '@/src/components/notAvailable';
import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';

import { RootState } from '@/src/reducers/types';
import { useGetLookerReports } from '@/src/components/project/Settings/components/Account/Dashboard/hooks/useGetLookerReports';
import { useBreakpoint } from '@/src/hooks/useBreakpoint';

const ProjectReports: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { lookerReports } = useGetLookerReports(projectId, {
    config: { enabled: !!projectId },
  });
  const { showLookerReport } = useSelector((store: RootState) => store.project.Selected);
  const [activeTab, setActiveTab] = useState(0);
  const tabListRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const contextualLookerReports = lookerReports;
  const { width } = useBreakpoint();

  // Check if tab list overflows horizontally
  useEffect(() => {
    const checkOverflow = () => {
      const ref = tabListRef.current;
      if (!ref) return;
      setShowScrollButtons(ref.scrollWidth > ref.clientWidth + 2); // +2 for rounding errors
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [contextualLookerReports, width]);

  const scrollTabs = (direction: 'left' | 'right') => {
    const ref = tabListRef.current;
    if (!ref) return;
    const scrollAmount = 200;
    if (direction === 'left') {
      ref.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    } else {
      ref.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const sidePanelStatus = window.localStorage.getItem('sidepanelStatus');
  const maxWidth = sidePanelStatus === 'default' ? width - 350 : width - 110;

  if (
    (!showLookerReport && showLookerReport !== undefined) ||
    contextualLookerReports?.length === 0
  ) {
    return <NotAvailable />;
  }

  return (
    <div
      className={clsx(
        'row content-row',
        '!tw-m-0',
        'tw-h-[calc(100vh-107px)]',
        'tw-overflow-y-auto tw-scroll-styles',
        'tw-overflow-x-hidden'
      )}
    >
      {/* Tabbed Looker Reports Section */}
      <div
        className={clsx('tw-w-full tw-h-full tw-flex tw-flex-col tw-bg-white tw-rounded tw-shadow')}
        style={{
          backgroundColor: '#f7f7f7',
          rowGap: '8px',
          display: 'inline-block',
          maxWidth: `calc(${maxWidth + 20}px)`,
        }}
      >
        {/* Tab group, separated from report by 16px gap */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            width: '100%',
            zIndex: 50,
            backgroundColor: 'transparent',
            paddingBottom: 4,
          }}
        >
          {showScrollButtons && (
            <Button
              className={clsx(
                'tw-absolute tw-left-0 tw-top-[40%] tw--translate-y-1/2 tw-z-10',
                'tw-bg-cfo-grey-600 tw-rounded-full tw-p-1',
                'tw-border tw-border-solid tw-border-cfo-grey-600',
                'hover:tw-bg-cfo-blue-600 active:tw-bg-cfo-blue-600'
              )}
              style={{ minWidth: 32, minHeight: 32, left: '12px' }}
              onClick={() => scrollTabs('left')}
              aria-label="Scroll left"
            >
              <Icon icon={ICONS.arrow_left} className="tw-text-[20px] tw-text-white" />
            </Button>
          )}
          <div
            ref={tabListRef}
            className={clsx('tw-flex tw-gap-4 tw-px-4 tw-pt-4')}
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              backgroundColor: 'white',
              borderRadius: '20px',
              margin: 0,
              marginBottom: 0,
              padding: '16px',
              scrollBehavior: 'smooth',
              paddingLeft: '40px',
              paddingRight: '40px',
            }}
          >
            {(contextualLookerReports || []).map((report, idx) => {
              const isLong = report.name && report.name.length > 150;
              const displayName = report.name
                ? isLong
                  ? report.name.slice(0, 150) + '…'
                  : report.name
                : `Report ${idx + 1}`;
              const tooltipId = `report-tab-tooltip-${idx}`;
              return (
                <React.Fragment key={report.name || idx}>
                  <button
                    className={clsx(
                      'tw-px-6 tw-py-2 tw-text-[16px] tw-font-medium tw-rounded-[12px] tw-transition-colors',
                      activeTab === idx
                        ? 'tw-bg-cfo-blue-600 tw-text-white'
                        : 'tw-bg-gray-200 tw-text-gray-700 hover:tw-bg-cfo-blue-100 hover:tw-text-cfo-blue-700'
                    )}
                    onClick={() => setActiveTab(idx)}
                    style={{ minWidth: 120, flex: '0 0 auto' }}
                    {...(isLong
                      ? {
                          'data-tooltip-id': tooltipId,
                          'data-tooltip-content': report.name,
                        }
                      : {})}
                  >
                    {displayName}
                  </button>
                  {isLong && <Tooltip id={tooltipId} content={report.name} place="top" />}
                </React.Fragment>
              );
            })}
          </div>
          {showScrollButtons && (
            <Button
              className={clsx(
                'tw-absolute tw-right-0 tw-top-[40%] tw--translate-y-1/2 tw-z-10',
                'tw-bg-cfo-grey-600 tw-rounded-full tw-p-1',
                'tw-border tw-border-solid tw-border-cfo-grey-600',
                'hover:tw-bg-cfo-blue-600 active:tw-bg-cfo-blue-600'
              )}
              style={{ minWidth: 32, minHeight: 32 }}
              onClick={() => scrollTabs('right')}
              aria-label="Scroll right"
            >
              <Icon icon={ICONS.arrow_right} className="tw-text-[20px] tw-text-white" />
            </Button>
          )}
        </div>
        <div style={{ height: 16 }} />
        {/* Displayed report */}
        <div
          className={clsx('tw-flex-1 tw-w-full tw-h-full tw-p-4')}
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'white',
            margin: '12px',
            borderRadius: '20px',
            marginTop: 0,
          }}
        >
          <div style={{ height: '90vh', position: 'relative' }}>
            {contextualLookerReports?.map((report, idx) => (
              <div
                key={report.name || idx}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: activeTab === idx ? 2 : 1,
                  opacity: activeTab === idx ? 1 : 0,
                  pointerEvents: activeTab === idx ? 'auto' : 'none',
                  transition: 'opacity 0s',
                  display: !report.url ? 'none' : undefined,
                }}
              >
                <LookerReport reportUrl={report.url} bare active={activeTab === idx} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectReports;
